import { time } from "console";
import { Bid } from "../models";
import { Message, MessageItem } from "../models/Chat";
import { Lane } from "../models/lane";
import { loadCategoriesMap } from "./constants";

type EquipmentType = "trailer" | "boxTruck" | "container" | "tractor" | string;
type TrailerSubType =
  | "DryVan"
  | "Reefer"
  | "FlatBed"
  | "Chassis"
  | string
  | symbol;
interface EquipmentModel {
  type: EquipmentType;
  trailerSubType?: TrailerSubType | null;
}
type LatLng = { lat: number; lng: number };

const handleMapIcon = (equipment: EquipmentModel): string => {
  const iconPath = "/images/"; // Add your icons base path here

  const iconMap: Record<
    EquipmentType,
    Partial<Record<TrailerSubType | "default", string | null>>
  > = {
    trailer: {
      DryVan: "Dry-Van-trailer.svg",
      Reefer: "Reefer-trailer.svg",
      Flatbed: "FlatBed-trailer.svg",
      Chassis: "Chassis-trailer.svg",
    },
    boxTruck: {
      DryVan: "BoxTruck-Dry.svg",
      Reefer: "BoxTruck-Reefer.svg",
    },
    container: {
      DryVan: "Container-dry.svg",
      Dry: "Container-dry.svg",
      Reefer: "Container-reefer.svg",
    },
    tractor: {
      default: "tractor.svg",
    },
  };

  if (!equipment?.type) return `${iconPath}hard-drive.svg`;

  const typeMap = iconMap[equipment.type];
  const icon =
    equipment.trailerSubType && typeMap[equipment.trailerSubType]
      ? typeMap[equipment.trailerSubType]
      : typeMap.default || "hard-drive.svg";

  return `${iconPath}${icon}`;
};

const formatEpochSecondsDate = (unixEpochSeconds: number) => {
  const requestedDate = new Date(unixEpochSeconds * 1000); // Convert seconds to milliseconds

  // Format: "Aug 20, 2025"
  const formattedDate = new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(requestedDate);

  return formattedDate;
};

const formatGroupedMessageDate = (unixEpochSeconds: number): string => {
  const requestedDate = new Date(unixEpochSeconds * 1000); // Convert seconds to ms
  const now = new Date();

  // Normalize to remove time part
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const requested = new Date(
    requestedDate.getFullYear(),
    requestedDate.getMonth(),
    requestedDate.getDate()
  );

  const diffInMs = today.getTime() - requested.getTime();
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  if (diffInDays === 0) return "Today";
  if (diffInDays === 1) return "Yesterday";
  if (diffInDays > 1 && diffInDays < 7) {
    // Show weekday name
    return requested.toLocaleDateString("en-US", { weekday: "long" });
  }

  // Otherwise → full format like "Aug 24, 2025"
  return requested.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const groupMessagesByDateAndUnread = (
  messages: Message[],
  unreadCount: number,
  companyID?: string
): MessageItem[] => {
  const items: MessageItem[] = [];
  let lastDate: string | null = null;

  messages.forEach((msg) => {
    const dateLabel = formatGroupedMessageDate(msg.timestamp); // e.g. Aug 25, 2025
    if (dateLabel !== lastDate) {
      items.push({ type: "date", label: dateLabel });
      lastDate = dateLabel;
    }

    // insert unread divider before first unread
    if (
      unreadCount > 0 &&
      !msg.readers.includes(companyID!) &&
      !items.some((i) => i.type === "unread")
    ) {
      items.push({ type: "unread", count: unreadCount });
    }

    items.push({ type: "message", data: msg });
  });

  return items;
};

const formatFullDate = (date: string) => {
  return new Date(date).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const formatRelativeTime = (iso: string): string => {
  const now = new Date();
  const date = new Date(iso);

  // Strip time for accurate date comparison
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  const dateOnly = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  );

  if (dateOnly.getTime() === today.getTime()) {
    // Same day → show hour:min with AM/PM
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  }

  if (dateOnly.getTime() === yesterday.getTime()) {
    return "Yesterday";
  }

  // Otherwise → show MM/dd/YY
  return date.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "2-digit",
  });
};

const formatLoadingTime = (time: number) => {
  if (time < 60) {
    return `${time} minute${time > 1 ? "s" : ""}`;
  } else {
    const mins = time % 60;
    const formatedmin = mins.toString().padStart(2, "0");
    const hours = Math.floor(time / 60);
    return `${hours} hour${hours > 1 ? "s" : ""}, ${
      mins > 0 ? `${formatedmin} min${mins > 1 ? "s" : ""}` : ""
    }`;
  }
};

const getExpiryDays = (unixEpochSeconds: number): string => {
  const now = Date.now(); // Avoid creating Date objects unnecessarily
  const targetTime = unixEpochSeconds * 1000;
  const diffInMs = targetTime - now;

  if (diffInMs <= 0) return "Expired";

  const diffInSeconds = Math.floor(diffInMs / 1000);
  if (diffInSeconds < 60) {
    return `${diffInSeconds} second${diffInSeconds !== 1 ? "s" : ""}`;
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return `${diffInMinutes} minute${diffInMinutes !== 1 ? "s" : ""}`;
  }

  const diffInDays = Math.ceil(diffInMs / (1000 * 60 * 60 * 24));
  return `${diffInDays} day${diffInDays !== 1 ? "s" : ""}`;
};

const getBidStatus = (status: string) => {
  switch (status) {
    case "accepted":
      return "Accepted";
    case "rejected":
      return "Rejected";
    case "cancelled":
      return "Cancelled";
    case "expired":
      return "Expired";
    case "pending":
      return "Pending";
    default:
      return "Unknown";
  }
};

const scrollbar = {
  hidden: {
    "&::-webkit-scrollbar": { width: 0, background: "transparent" },
    "&::-webkit-scrollbar-thumb": { display: "none" },
  },
  styled: {
    "&::-webkit-scrollbar": { width: "7px", height: "7px" },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#EBEEF2",
      borderRadius: "100px",
      margin: "4px",
    },
    "&::-webkit-scrollbar-thumb:hover": { backgroundColor: "#718096" },
    "&::-webkit-scrollbar-track": { background: "white" },
  },
};

const mapShadow = { base: "none", md: "0px 0px 8px 0px #1B242C14" };

const isAlphanumeric = ({
  value,
  maxChar,
}: {
  value: string;
  maxChar: number;
}) => {
  const regex = /^[a-zA-Z0-9]+$/;
  return regex.test(value) && value.length >= maxChar;
};

const formatDate = (date: Date, hour = true) =>
  date.toLocaleDateString("en-US", {
    month: "short",
    year: "numeric",
    day: "2-digit",
    hour: hour ? "2-digit" : undefined,
    minute: hour ? "2-digit" : undefined,
  });

const indicators = {
  blue: { bg: "customBlue.100", color: "customBlue.900" },
  orange: { bg: "customOrange.100", color: "customOrange.900" },
  gray: { bg: "gray.100", color: "gray.900" },
};
const statusStyle: Record<
  LoadStatus,
  { bg: string; color: string; content: string }
> = {
  unassigned: { ...indicators.orange, content: "Driver Unassigned" },
  assigned: { ...indicators.blue, content: "Driver Assigned" },
  awaiting_pickup: { ...indicators.gray, content: "Heading To Pickup" },
  at_pickup_yard: { ...indicators.blue, content: "At Pickup" },
  checked_in_pickup: { ...indicators.gray, content: "Checked In" },
  loading_in_progress: { ...indicators.gray, content: "Loading In Progress" },
  loading_completed: { ...indicators.gray, content: "Loading Done" },
  sealed_and_ready: { ...indicators.blue, content: "Trailer Sealed" },
  in_transit: { ...indicators.gray, content: "In Transit" },
  late: { ...indicators.orange, content: "Late" },
  at_dropoff_yard: { ...indicators.blue, content: "At Drop-off" },
  checked_in_dropoff: { ...indicators.gray, content: "Checked In" },
  unloading_in_progress: {
    ...indicators.gray,
    content: "Unloading In Progress",
  },
  completed: { ...indicators.blue, content: "Load Completed" },
  cancelled: { ...indicators.orange, content: "Load Cancelled" },
  accepted: { ...indicators.blue, content: "Load Accepted" },
};
export type LoadStatus =
  | "unassigned" // State used when a driver is not yet assigned
  | "assigned" // state used when the driver is assigned
  | "awaiting_pickup" // Initial state — waiting for the driver
  | "at_pickup_yard" // Driver has arrived at pickup yard
  | "checked_in_pickup" // Driver is checked in for pickup
  | "loading_in_progress" // Loading started
  | "loading_completed" // Loading finished
  | "sealed_and_ready" // BOL uploaded + trailer sealed
  | "in_transit" // Driver has been checked out, load is in transit
  | "late" // Driver is late
  | "at_dropoff_yard" // Driver arrived at drop-off
  | "checked_in_dropoff" // Driver checked in for unloading
  | "unloading_in_progress" // Unloading started
  | "completed" // Unloading done, load is complete
  | "cancelled" // Load was cancelled
  | "accepted"; // Load was accepted by driver

const fakeAPIRequest = async (companyID: string) => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(companyID);
    }, 900);
  });
};
type DynamoDBAttributeValue =
  | { S: string }
  | { N: string }
  | { BOOL: boolean }
  | { NULL: boolean }
  | { SS: string[] }
  | { NS: string[] }
  | { L: DynamoDBAttributeValue[] }
  | { M: { [key: string]: DynamoDBAttributeValue } };

type PossiblyUnwrappedDynamoDBValue =
  | DynamoDBAttributeValue
  | { [key: string]: DynamoDBAttributeValue | any }; // handles nested objects like `load`

type DynamoDBItem = { [key: string]: PossiblyUnwrappedDynamoDBValue };

const unmarshallDynamoDB = (item: DynamoDBItem): Record<string, any> => {
  const result: Record<string, any> = {};

  for (const key in item) {
    const value = item[key];

    // Case: value is a valid DynamoDBAttributeValue
    if (isAttributeValue(value)) {
      result[key] = unmarshallDynamoDBValue(value);
    } else if (typeof value === "object" && value !== null) {
      // Nested object that might be a map (like "load", "driver")
      result[key] = unmarshallDynamoDB(value as DynamoDBItem);
    } else {
      // Unexpected format
      console.warn("Unknown DynamoDB type for key:", key, value);
    }
  }

  return result;
};

const isAttributeValue = (value: any): value is DynamoDBAttributeValue => {
  if (typeof value !== "object" || value === null) return false;
  const keys = Object.keys(value);
  return (
    keys.length === 1 &&
    ["S", "N", "BOOL", "NULL", "SS", "NS", "L", "M"].includes(keys[0])
  );
};

const showNotification = (title: string, body: string) => {
  if (Notification.permission === "granted") {
    new Notification(title, { body });
  } else if (Notification.permission !== "denied") {
    Notification.requestPermission().then((permission) => {
      if (permission === "granted") {
        new Notification(title, { body });
      }
    });
  }
};

const unmarshallDynamoDBValue = (value: DynamoDBAttributeValue): any => {
  if ("S" in value) return value.S;
  if ("N" in value) return Number(value.N);
  if ("BOOL" in value) return value.BOOL;
  if ("NULL" in value) return null;
  if ("SS" in value) return value.SS;
  if ("NS" in value) return value.NS.map(Number);
  if ("L" in value) return value.L.map(unmarshallDynamoDBValue);
  if ("M" in value) return unmarshallDynamoDB(value.M);
  return undefined;
};

const convertDistance = (
  value: number,
  fromUnit: string,
  toUnit: string
): number => {
  const ratio = 1.60934; // 1 mile = 1.60934 km
  if (fromUnit === toUnit) return value;
  return fromUnit === "mile" ? value * ratio : value / ratio;
};

const equipmentStatus: Record<number, string[]> = {
  0: ["pending"],
  1: ["at_pickup", "check_in_pickup"],
  2: ["in_transit"],
  3: ["at_dropoff"],
  4: ["archived"],
};

const countryAliasMap: Record<string, string> = {
  canada: "CA",
  ca: "CA",
  "united states": "US",
  usa: "US",
  us: "US",
  america: "US",
};

const normalizeSearchTerms = (input: string): string[] => {
  const normalized = input.trim().toLowerCase();
  const rawParts = normalized
    .split(/[\s,-]+/)
    .map((p) => p.trim())
    .filter((p) => p.length > 0);

  // Replace known country names with ISO codes
  return rawParts.map((part) => countryAliasMap[part] || part);
};

const mapApiToLaneModel = (data: any): Lane => ({
  laneID: data.laneID.toString(),
  dropoff: data.dropoff,
  pickup: data.pickup,
  price: data.price,
  endDate: data.endDate ? new Date(data.endDate) : undefined,
  startDate: data.startDate ? new Date(data.startDate) : undefined,
});

const mapApiBidToModel = (apiBid: any): Bid => {
  return {
    id: apiBid.SK?.split("#")[1] ?? "",
    askPrice: apiBid.askPrice,
    matchPrice:
      apiBid.matchPrice ??
      Number.parseFloat(
        Math.floor(apiBid.totalRevenue / apiBid.totalDistance).toFixed(2)
      ),
    totalRevenue: apiBid.totalRevenue,
    // amount: apiBid.askPrice,
    category: apiBid.category,
    distance: apiBid.totalDistance,
    status: apiBid.status,
    shipperName: apiBid.requester,
    shipperID: apiBid.requestBy,
    createdAt: apiBid.createdAt / 1000,
    expiresAt: apiBid.expiresAt / 1000,
    updatedAt: apiBid.updatedAt / 1000,
    driverAvailability: "",
    equipment: {
      companyID: apiBid.companyID,
      lp: apiBid.lp,
      num: apiBid.num,
      available: false, // default fallback
      equipmentID: "--", // placeholder until provided by API
      trailerSubType: apiBid.trailerSubType,
      type: apiBid.type,
      driverID: apiBid.driverID ?? undefined,
    },
    loadData: {
      loadID: apiBid.loadID,
      sealBreaker: apiBid.sealBreaker ?? "",
      category:
        !apiBid.category && !apiBid.loadDetails?.category
          ? "--"
          : loadCategoriesMap[
              `${apiBid.category ?? apiBid.loadDetails.category}`
            ],
      loadNumID: apiBid.loadDetails?.loadNumID ?? apiBid.loadID,
      pickupTime: apiBid.pickupTime ? apiBid.pickupTime / 1000 : undefined,
      dropOffTime: apiBid.dropOffTime ? apiBid.dropOffTime / 1000 : undefined,
      pickupLocation: apiBid.pickupLocation ?? {
        name: "--",
        address: "--",
        geofencePoints: [],
      },
      equipmentStatus: apiBid.equipmentStatus ?? "",
      dropOffLocation: apiBid.dropOffLocation ?? {
        name: "--",
        address: "--",
        geofencePoints: [],
      },
      delay: apiBid.delay,
      unloadDuration: apiBid.unloadDuration,
      carrierSecurity: apiBid.carrierSecurity ?? [],
    },
  };
};

const isEmailValid = (email: string) => /\S+@\S+\.\S+/.test(email);

let hereMapsLoaded = false;

const loadHereMaps = async (): Promise<void> => {
  if (hereMapsLoaded) {
    return;
  }
  return new Promise(function (resolve, reject) {
    const script = document.createElement("script");
    script.src = `https://js.api.here.com/v3/3.1/mapsjs.bundle.js`;
    script.async = true;
    script.defer = true;
    script.type = "module";
    script.onload = () => {
      hereMapsLoaded = true;
      resolve();
    };
    script.onerror = reject;
    document.head.appendChild(script);
  });
};

const sphericalCenter = (coords: LatLng[]) => {
  let hyp;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const toDeg = (r: number) => (r * 180) / Math.PI;
  let point = coords.reduce(
    function (acc, val) {
      const latRad = toRad(val.lat);
      const lngRad = toRad(val.lng);
      acc.x += Math.cos(latRad) * Math.cos(lngRad);
      acc.y += Math.cos(latRad) * Math.sin(lngRad);
      acc.z += Math.sin(latRad);
      return acc;
    },
    { x: 0, y: 0, z: 0 }
  );
  point.x /= coords.length;
  point.y /= coords.length;
  point.z /= coords.length;
  hyp = Math.sqrt(point.x ^ (2 + point.y) ^ 2);
  return {
    lat: toDeg(Math.atan2(point.z, hyp)),
    lng: toDeg(Math.atan2(point.y, point.x)),
  };
};

const coordinatesArrayToMap = (
  coords: { lat: number; lon: number }[]
): LatLng[] => {
  return coords.map(({ lat, lon }) => Object.assign({}, { lat, lng: lon }));
};

export {
  fakeAPIRequest,
  formatDate,
  isAlphanumeric,
  isEmailValid,
  formatEpochSecondsDate,
  getBidStatus,
  mapApiBidToModel,
  mapApiToLaneModel,
  convertDistance,
  getExpiryDays,
  handleMapIcon,
  normalizeSearchTerms,
  formatFullDate,
  formatRelativeTime,
  groupMessagesByDateAndUnread,
  showNotification,
  unmarshallDynamoDB,
  loadHereMaps,
  coordinatesArrayToMap,
  sphericalCenter,
  formatLoadingTime,
  equipmentStatus,
  statusStyle,
  scrollbar,
  mapShadow,
};
